/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx } from '@emotion/react';
import React, { useMemo } from 'react';
import Lottie from 'lottie-react';
import Icon from '../icons/Icon';
import CircularSpinner from '../assets/CircularSpinner.png';
import './Loader.scss';
import { SxProp } from '../style/sxProp';
import useTheme from '../style/useTheme';
import { ColorResolveOptions } from '../style/colors';

const DEFAULT_SPINNER_SIZE = 48;

export type SpinnerInfo =
  | {
      type: 'logo';
    }
  | {
      type: 'img';
      src: string;
    }
  | {
      type: 'lottie';
      width?: number;
      height?: number;
      data: unknown;
    }
  | {
      type: 'svg';
      data: string;
    }
  | { type: undefined | 'circle' }
  | {
      type: 'dots';
      size?: number;
      color?: ColorResolveOptions;
    };

export type Props = {
  text?: string;
  // It is being used
  // eslint-disable-next-line react/no-unused-prop-types
  sx?: SxProp;
  containerHeight?: string | number;
  containerWidth?: string | number;
} & SpinnerInfo;

export type SpinnerProps = Omit<Props, 'containerHeight' | 'containerWidth'> &
  SpinnerInfo;

const Spinner: React.FC<SpinnerProps> = ({ sx, ...rest }) => {
  const { resolveCSS } = useTheme();
  const css = useMemo(() => resolveCSS(sx), [sx, resolveCSS]);
  switch (rest.type) {
    case 'logo':
      return (
        <Icon
          sx={sx}
          icon="pinwheelLogo"
          className="origami-Loader__spinner"
          color={{
            type: 'theme',
            colorLegacy: 'Primary/50',
            colorNext: 'brand/default',
          }}
          size={DEFAULT_SPINNER_SIZE}
          viewBox="0 0 32 32"
        />
      );
    case 'lottie':
      return (
        <Lottie
          height={rest.width || DEFAULT_SPINNER_SIZE}
          width={rest.height || DEFAULT_SPINNER_SIZE}
          animationData={rest.data}
          loop
          autoplay
          style={{
            height: `${rest.width || DEFAULT_SPINNER_SIZE}px`,
            width: `${rest.height || DEFAULT_SPINNER_SIZE}px`,
          }}
        />
      );
    case 'img':
      return (
        <img
          css={{
            maxWidth: `${DEFAULT_SPINNER_SIZE}px`,
            maxHeight: `${DEFAULT_SPINNER_SIZE}px`,
            ...css,
          }}
          alt="Spinning circle"
          src={rest.src}
          data-testid="Loader-Circle-Spinner"
          className="origami-Loader__spinner origami-Loader__spinner--reverse"
        />
      );
    case 'dots':
      return (
        <Icon
          sx={sx}
          icon="dotsSpinner"
          className="origami-Loader__spinner origami-Loader__spinner--reverse"
          color={
            rest.color || {
              type: 'theme',
              colorLegacy: 'Primary/50',
              colorNext: 'brand/default',
            }
          }
          size={rest.size}
        />
      );
    default:
      return (
        <img
          css={{
            maxWidth: `${DEFAULT_SPINNER_SIZE}px`,
            maxHeight: `${DEFAULT_SPINNER_SIZE}px`,
            ...css,
          }}
          alt="Spinning circle"
          src={CircularSpinner}
          data-testid="Loader-Circle-Spinner"
          className="origami-Loader__spinner origami-Loader__spinner--reverse"
        />
      );
  }
};

const Loader: React.FC<Props> = (props) => {
  const { text, containerHeight, containerWidth } = props;
  return (
    <div
      data-testid="Loader"
      className="origami-Loader"
      style={{
        height: containerHeight ?? '100%',
        width: containerWidth ?? '100%',
      }}
    >
      <div
        className="origami-Loader__spinner-container"
        // Note: we've historically had 100px hardcoded as the container height in the past, so this is preserved here
        // for consistency. In the future, we probably want to revisit this styling and potentially remove this, so that
        // the loader would be fully centered instead of being slightly offset upwards.
        style={{ height: containerHeight ?? '100px' }}
      >
        <Spinner {...props} />
      </div>
      {text ? (
        <div className="origami-Loader__spinner-text" data-testid="Loader-text">
          {text}
        </div>
      ) : null}
    </div>
  );
};

export default Loader;
